import type { InstrumentPrices } from '@/common/types'
import { getLastPrice, getMinShares, getInvestmentAmount } from '@/trading/utils'

export function calculateInitialAllocation(
    instruments: { weight: number; priceInfo: InstrumentPrices; securityCode: string }[],
    totalInvestment?: number,
): { securityCode: string; units: number; amount: number; relevantPrice: number }[] {
    if (!instruments || instruments.length === 0) return []

    const weights = instruments.map((i) => i.weight)
    const totalWeight = weights.reduce((a, b) => a + b, 0)
    const minWeight = Math.min(...weights)

    if (!isFinite(minWeight) || minWeight <= 0) return []

    const slippageFactor = 1.02

    function isValidPrice(price?: number): price is number {
        return typeof price === 'number' && isFinite(price) && price > 0
    }

    function fallbackTargetAmount(index: number) {
        const minIndex = weights.indexOf(minWeight)
        const minInst = instruments[minIndex]
        const minPrice = getLastPrice(minInst.priceInfo)
        const minShares = getMinShares('BUY', 0, minPrice)
        const investmentAmount = getInvestmentAmount(minShares, minPrice)
        return investmentAmount.valueWithPrecision(2) * (weights[index] / 100)
    }

    function targetAmountFor(instIndex: number, instWeight: number) {
        if (typeof totalInvestment === 'number' && isFinite(totalInvestment) && totalInvestment > 0) {
            const weightPortion = totalWeight > 0 ? instWeight / totalWeight : 0
            return totalInvestment * weightPortion
        }
        return fallbackTargetAmount(instIndex)
    }

    function computeUnitsAndAmount(targetAmount: number, price: number) {
        const minSharesForThis = getMinShares('BUY', 0, price)
        let units = Math.floor(targetAmount / (price * slippageFactor))
        if (units < minSharesForThis) units = minSharesForThis
        const amount = getInvestmentAmount(units, price).valueWithPrecision(2)
        return { units, amount }
    }

    return instruments.map((inst, idx) => {
        const price = getLastPrice(inst.priceInfo)
        if (!isValidPrice(price)) {
            return { securityCode: inst.securityCode, units: 0, amount: 0, relevantPrice: 0 }
        }

        const amountTarget = targetAmountFor(idx, inst.weight)
        const { units, amount } = computeUnitsAndAmount(amountTarget, price)

        return { securityCode: inst.securityCode, units, amount, relevantPrice: price }
    })
}

export default calculateInitialAllocation
